/**
 * GameCanvas.jsx
 * 
 * DOM/SVG-based game canvas using the new rendering system.
 * Replaces canvas-based rendering with DOM elements for better visuals and effects.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useGameInput, controlMap } from '../hooks/useGameInput';
import {
  buildPlayableStage,
  makeFighter,
  updateFighterTimers,
  applyControls,
  updatePhysics,
  updateCpu,
  resolveCombat,
  checkStocks,
  updateCamera,
} from '../game/engine';
import { NeonCity, VolcanicArena, AncientTemple, SpaceStation, Battlefield } from './stages';
import { GameContainer, FighterSvg, GameHud, ComboDisplay, EffectsSystem } from './game';
import { KillCamReplay } from './KillCamReplay';

const GAME_WIDTH = 1200;
const GAME_HEIGHT = 600;

const stageComponents = {
  neonPier: NeonCity,
  reactorCore: VolcanicArena,
  skySpire: AncientTemple,
  ironRing: SpaceStation,
  battlefield: Battlefield,
};

export function GameCanvas({
  playerCharacter,
  cpuCharacter,
  stagePreset,
  mode = 'cpu',
  onMatchEnd,
  onKillCam,
  isActive = true,
}) {
  const gameContainerRef = useRef(null);
  const leftFighterRef = useRef(null);
  const rightFighterRef = useRef(null);
  const comboRef = useRef(null);
  const effectsRef = useRef(null);
  const animationRef = useRef(null);
  const lastTickRef = useRef(0);
  const gameStateRef = useRef(null);
  const stagePresetRef = useRef(stagePreset);
  const frameCountRef = useRef(0);
  const killCamBufferRef = useRef([]);
  const lastKillCamSnapshotAtRef = useRef(0);

  // Keep stagePresetRef always up to date with latest prop
  stagePresetRef.current = stagePreset;

  const { isPressed, consumePressedOnce } = useGameInput(isActive);

  // UI state - triggers re-renders
  const [uiState, setUiState] = useState({
    running: false,
    winner: null,
    initialized: false,
    gameTime: 0,
  });
  const [killCamReplay, setKillCamReplay] = useState(null);
  const [showKillCam, setShowKillCam] = useState(false);

  // Initialize game state ref
  if (!gameStateRef.current) {
    gameStateRef.current = {
      running: false,
      left: null,
      right: null,
      lastTick: 0,
      winner: null,
      mode,
      matchEndAt: 0,
      stage: buildPlayableStage(stagePresetRef.current),
      camera: { x: 0, y: 0 },
      gameTime: 0,
    };
  }

  const gameState = gameStateRef.current;

  const updateUiFromGameState = useCallback(() => {
    setUiState(prev => ({
      ...prev,
      running: gameState.running,
      winner: gameState.winner,
      initialized: true,
      gameTime: gameState.gameTime,
    }));
  }, [gameState]);

  const snapshotFighter = (fighter) => {
    if (!fighter) return null;

    return {
      x: fighter.x,
      y: fighter.y,
      vx: fighter.vx,
      vy: fighter.vy,
      hp: fighter.hp,
      stocks: fighter.stocks,
      facing: fighter.facing,
      width: fighter.width,
      height: fighter.height,
      slot: fighter.slot,
      hitstun: fighter.hitstun,
      attackCooldown: fighter.attackCooldown,
      onGround: fighter.onGround,
    };
  };

  const captureKillCamSnapshot = useCallback(() => {
    if (!gameState.left || !gameState.right) return;

    const elapsedMs = Math.round(gameState.gameTime * 1000);
    if (elapsedMs - lastKillCamSnapshotAtRef.current < 100) return;

    lastKillCamSnapshotAtRef.current = elapsedMs;
    killCamBufferRef.current.push({
      t: elapsedMs,
      p1: snapshotFighter(gameState.left),
      p2: snapshotFighter(gameState.right),
    });

    const cutoff = elapsedMs - 5000;
    killCamBufferRef.current = killCamBufferRef.current.filter(frame => frame.t >= cutoff);
  }, [gameState]);

  const buildKillCamReplay = useCallback((winner, reason = 'ko', loser = null) => {
    const frames = killCamBufferRef.current;
    if (!frames.length) return null;

    const firstTime = frames[0].t;
    return {
      version: 1,
      matchId: `local-${Date.now()}`,
      stageId: stagePresetRef.current?.id || 'battlefield',
      winner,
      loser: loser?.name || null,
      reason,
      localPlayerSlot: 'p1',
      durationMs: frames[frames.length - 1].t - firstTime,
      createdAt: Date.now(),
      characters: {
        p1: playerCharacter,
        p2: mode === 'cpu' ? cpuCharacter : playerCharacter,
      },
      frames: frames.map(frame => ({ ...frame, t: frame.t - firstTime })),
    };
  }, [cpuCharacter, mode, playerCharacter]);

  const startGame = () => {
    if (!playerCharacter) return;

    const currentPreset = stagePresetRef.current;
    const enemy = mode === 'cpu' ? cpuCharacter || playerCharacter : playerCharacter;
    
    gameState.stage = buildPlayableStage(currentPreset);
    gameState.left = makeFighter(playerCharacter, gameState.stage.spawnLeft, gameState.stage, 'p1');
    gameState.right = makeFighter(enemy, gameState.stage.spawnRight, gameState.stage, 'p2');
    gameState.mode = mode;
    gameState.winner = null;
    gameState.running = true;
    gameState.lastTick = performance.now();
    gameState.matchEndAt = 0;
    gameState.gameTime = 0;
    frameCountRef.current = 0;
    killCamBufferRef.current = [];
    lastKillCamSnapshotAtRef.current = 0;
    setKillCamReplay(null);
    setShowKillCam(false);
    
    updateCamera(gameState.camera, [gameState.left, gameState.right], gameState.stage, 1, true);
    updateUiFromGameState();

    // Clear effects
    effectsRef.current?.clear();
    comboRef.current?.clear();
  };

  const updateGame = useCallback((dt) => {
    const fighters = [gameState.left, gameState.right];

    // Update timers
    for (const fighter of fighters) {
      updateFighterTimers(fighter, dt);
    }

    // Apply controls
    const pressedOnce = consumePressedOnce();
    applyControls(
      gameState.left,
      controlMap.p1,
      (key) => isPressed(key),
      (key) => pressedOnce.has(key),
      dt
    );

    if (gameState.mode === 'local') {
      applyControls(
        gameState.right,
        controlMap.p2,
        (key) => isPressed(key),
        (key) => pressedOnce.has(key),
        dt
      );
    } else {
      updateCpu(gameState.right, gameState.left, dt);
    }

    // Update physics
    for (const fighter of fighters) {
      updatePhysics(fighter, gameState.stage, dt);
    }

    // Resolve combat
    const leftHpBefore = gameState.left.hp;
    const rightHpBefore = gameState.right.hp;

    resolveCombat(gameState.left, gameState.right);
    resolveCombat(gameState.right, gameState.left);

    // Check for hits and spawn effects
    const leftAfter = gameState.left;
    const rightAfter = gameState.right;

    // Left fighter hit right fighter
    if (rightAfter.hp > rightHpBefore) {
      // Spawn hit effect at right fighter position
      effectsRef.current?.spawnHitParticles(
        rightAfter.x + rightAfter.width / 2,
        rightAfter.y + rightAfter.height / 2,
        8,
        '#ffffff',
        1 + (leftAfter.hp / 100)
      );
      effectsRef.current?.spawnImpact(
        rightAfter.x + rightAfter.width / 2,
        rightAfter.y + rightAfter.height / 2,
        '#ff6b6b',
        40 + leftAfter.hp / 3
      );
      
      // Trigger hit reaction on right fighter
      rightFighterRef.current?.triggerHit();
      
      // Screen shake
      gameContainerRef.current?.shake(Math.min(8, 3 + leftAfter.hp / 30), 150);
    }

    // Right fighter hit left fighter
    if (leftAfter.hp > leftHpBefore) {
      // Spawn hit effect at left fighter position
      effectsRef.current?.spawnHitParticles(
        leftAfter.x + leftAfter.width / 2,
        leftAfter.y + leftAfter.height / 2,
        8,
        '#ffffff',
        1 + (rightAfter.hp / 100)
      );
      effectsRef.current?.spawnImpact(
        leftAfter.x + leftAfter.width / 2,
        leftAfter.y + leftAfter.height / 2,
        '#ff6b6b',
        40 + rightAfter.hp / 3
      );
      
      // Trigger hit reaction on left fighter
      leftFighterRef.current?.triggerHit();
      
      // Screen shake
      gameContainerRef.current?.shake(Math.min(8, 3 + rightAfter.hp / 30), 150);
    }

    // Check stocks
    let stockLossReplay = null;
    const result = checkStocks(fighters, gameState.stage, ({ fighter, reason }) => {
      const survivor = fighters.find(candidate => candidate !== fighter && candidate.stocks > 0);
      stockLossReplay = buildKillCamReplay(survivor?.name || 'Survivor', reason, fighter);
      if (stockLossReplay) {
        setKillCamReplay(stockLossReplay);
        setShowKillCam(true);
        onKillCam?.(stockLossReplay);
      }
    });
    if (result.gameOver) {
      gameState.running = false;
      gameState.winner = result.winner?.name;
      gameState.matchEndAt = performance.now() + 1500;
      const replay = stockLossReplay || buildKillCamReplay(gameState.winner);
      if (replay) {
        setKillCamReplay(replay);
        setShowKillCam(true);
        if (!stockLossReplay) onKillCam?.(replay);
      }
      updateUiFromGameState();
      if (onMatchEnd) {
        onMatchEnd({
          winner: gameState.winner,
          mode: gameState.mode,
          timestamp: Date.now(),
        });
      }
    }

    // Update game time
    gameState.gameTime += dt;
    captureKillCamSnapshot();
    
    // Update React state periodically (every 30 frames ~ 0.5s) to show timer
    frameCountRef.current++;
    if (frameCountRef.current % 30 === 0) {
      setUiState(prev => ({
        ...prev,
        gameTime: gameState.gameTime,
      }));
    }
  }, [gameState, isPressed, consumePressedOnce, onMatchEnd, onKillCam, updateUiFromGameState, buildKillCamReplay, captureKillCamSnapshot]);

  const gameLoop = useCallback((now) => {
    const dt = Math.min(0.033, (now - lastTickRef.current) / 1000 || 0.016);
    lastTickRef.current = now;

    if (gameState.running) {
      updateGame(dt);
    }

    // Update camera
    updateCamera(gameState.camera, [gameState.left, gameState.right], gameState.stage, dt, !gameState.left || !gameState.right);

    // Update DOM positions (60fps direct manipulation)
    if (gameState.left && leftFighterRef.current) {
      leftFighterRef.current.setPosition(gameState.left.x, gameState.left.y);
      leftFighterRef.current.setFacing(gameState.left.facing === -1);
    }
    if (gameState.right && rightFighterRef.current) {
      rightFighterRef.current.setPosition(gameState.right.x, gameState.right.y);
      rightFighterRef.current.setFacing(gameState.right.facing === -1);
    }

    // Update camera transform
    if (gameContainerRef.current) {
      gameContainerRef.current.setCamera(gameState.camera.x, gameState.camera.y, 1);
    }

    animationRef.current = requestAnimationFrame(gameLoop);
  }, [gameState, updateGame]);

  // Start/stop game loop
  useEffect(() => {
    if (!isActive) return;

    lastTickRef.current = performance.now();
    animationRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, gameLoop]);

  // Reset game state when stagePreset changes
  useEffect(() => {
    if (gameStateRef.current) {
      const currentPreset = stagePresetRef.current;
      gameStateRef.current.stage = buildPlayableStage(currentPreset);
      
      if (!gameStateRef.current.running) {
        updateCamera(gameStateRef.current.camera, [gameStateRef.current.left, gameStateRef.current.right], gameStateRef.current.stage, 1, true);
      }
    }
  }, [stagePreset]);

  // Auto-start game when characters are ready
  useEffect(() => {
    if (playerCharacter && !gameState.running && !gameState.winner) {
      gameState.stage = buildPlayableStage(stagePresetRef.current);
      startGame();
    }
  }, [playerCharacter, cpuCharacter, gameState.running, gameState.winner]);

  // Get the SVG component for the current stage
  const StageBackground = stageComponents[stagePreset?.id] || Battlefield;

  return (
    <>
      {showKillCam && killCamReplay && (
        <KillCamReplay replay={killCamReplay} onClose={() => setShowKillCam(false)} />
      )}
      {/* Game Container - everything contained within */}
      <GameContainer
        ref={gameContainerRef}
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        backgroundComponent={StageBackground}
        backgroundProps={{ animate: true }}
        overlayChildren={
          <>
            {/* Effects layer */}
            <EffectsSystem ref={effectsRef} width={GAME_WIDTH} height={GAME_HEIGHT} />
            <ComboDisplay ref={comboRef} width={GAME_WIDTH} height={GAME_HEIGHT} />
            
            {/* HUD layer */}
            <GameHud
              gameState={gameState}
              leftCharacter={playerCharacter}
              rightCharacter={mode === 'cpu' ? cpuCharacter : playerCharacter}
              gameTime={uiState.gameTime}
              isLocalPlayerLeft={true}
            />
            
            {/* Start/Rematch Button */}
            {uiState.initialized && !uiState.running && (
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: GAME_WIDTH,
                  height: GAME_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  pointerEvents: 'auto',
                }}
              >
                <button
                  onClick={startGame}
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-xl text-lg hover:from-cyan-400 hover:to-blue-500 transition-all transform hover:scale-105 shadow-lg"
                >
                  {uiState.winner ? 'Rematch' : 'Start Match'}
                </button>
              </div>
            )}
            
            {/* Winner display */}
            {uiState.winner && (
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: GAME_WIDTH,
                  height: GAME_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <div className="text-6xl font-black text-white drop-shadow-lg animate-bounce">
                  {uiState.winner} Wins!
                </div>
              </div>
            )}
          </>
        }
      >
        {/* Fighters in world layer */}
        {gameState.left && (
          <FighterSvg
            ref={leftFighterRef}
            fighter={gameState.left}
            characterId={playerCharacter?.archetype}
            isLocal={true}
          />
        )}
        {gameState.right && (
          <FighterSvg
            ref={rightFighterRef}
            fighter={gameState.right}
            characterId={mode === 'cpu' ? cpuCharacter?.archetype : playerCharacter?.archetype}
            isLocal={false}
          />
        )}
      </GameContainer>

      {/* Controls Hint - outside game area */}
      <div 
        className="mt-4 flex justify-between text-xs text-white/50 bg-black/50 backdrop-blur-sm rounded-lg p-3"
        style={{ maxWidth: GAME_WIDTH, marginLeft: 'auto', marginRight: 'auto' }}
      >
        <div>
          <span className="font-bold text-cyan-400">P1:</span> WASD Move • F Attack • G Special • Shift Dash
        </div>
        <div>
          {mode === 'local' ? (
            <span><span className="font-bold text-pink-400">P2:</span> Arrows Move • / Attack • . Special • RShift Dash</span>
          ) : (
            <span><span className="font-bold text-pink-400">CPU:</span> Auto-controlled</span>
          )}
        </div>
      </div>
    </>
  );
}

export default GameCanvas;
